// Sign in and register. Shown only when GET /auth/me answered and nobody is
// signed in, so an unreachable API never locks a traveller out of a cached
// diary. One column, white, no decoration, 44px targets.
import { useState } from "react";
import { Icon, IconLock } from "@traveldiary/ui";
import { ApiError } from "../lib/api";
import { useApp } from "../state/app";

type Mode = "login" | "register";

export function AuthScreen() {
  const { t, signIn, signUp } = useApp();
  const [mode, setMode] = useState<Mode>("login");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit =
    username.trim().length > 0 &&
    password.length > 0 &&
    (mode === "login" || displayName.trim().length > 0);

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!canSubmit || busy) return;
    setBusy(true);
    setError(null);
    try {
      if (mode === "login") await signIn(username, password);
      else await signUp({ username, password, displayName });
    } catch (cause) {
      const permanent = cause instanceof ApiError && cause.isPermanent;
      setError(permanent ? t("auth.rejected") : t("auth.unreachable"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="screen-pad auth-screen">
      <form className="auth" onSubmit={onSubmit}>
        <h2 className="auth-title">{mode === "login" ? t("auth.signIn") : t("auth.signUp")}</h2>
        <p className="auth-lede">{t("auth.lede")}</p>

        <label className="field">
          <span className="field-label">{t("auth.username")}</span>
          <input
            className="input"
            name="username"
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            value={username}
            onChange={(event) => setUsername(event.target.value)}
          />
        </label>

        {mode === "register" ? (
          <label className="field">
            <span className="field-label">{t("auth.displayName")}</span>
            <input
              className="input"
              name="displayName"
              autoComplete="name"
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
            />
          </label>
        ) : null}

        <label className="field">
          <span className="field-label">{t("auth.password")}</span>
          <input
            className="input"
            name="password"
            type="password"
            autoComplete={mode === "login" ? "current-password" : "new-password"}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </label>

        {error ? (
          <p className="auth-error" role="alert">
            {error}
          </p>
        ) : null}

        <button type="submit" className="btn btn-primary btn-block" disabled={!canSubmit || busy}>
          {busy ? t("auth.working") : mode === "login" ? t("auth.signIn") : t("auth.signUp")}
        </button>

        <button
          type="button"
          className="btn btn-quiet btn-block"
          onClick={() => {
            setMode(mode === "login" ? "register" : "login");
            setError(null);
          }}
        >
          {mode === "login" ? t("auth.toRegister") : t("auth.toLogin")}
        </button>

        <p className="auth-hint">
          <Icon size={14} color="var(--color-text-muted)">
            <IconLock />
          </Icon>
          {t("auth.demoHint")}
        </p>
      </form>
    </div>
  );
}
